import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Building, Plus, Edit, Trash2, Download, Upload, ChevronDown, ChevronUp, Check, X, FileText, AlertCircle, Eye, MapPin, Hash } from 'lucide-react';
import { SuccessNotification, SUCCESS_CONFIGS } from '../components/SuccessNotification';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Immeubles = () => {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [viewingBuilding, setViewingBuilding] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortField, setSortField] = useState('nom');
  const [sortOrder, setSortOrder] = useState('asc');
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    num_immeuble: '',
    nom: '',
    adresse: '',
    code_postal: '',
    ville: '',
    immatriculation: '',
    nb_lots: 0,
    total_tantiemes: 10000,
    // Coordonnées bancaires de la copropriété
    banque_nom: '',
    banque_iban: '',
    banque_bic: '',
    banque_titulaire: ''
  });

  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successConfig, setSuccessConfig] = useState(null);
  const [notificationType, setNotificationType] = useState('BUILDING_CREATED');

  useEffect(() => { fetchBuildings(); }, [token]);

  const fetchBuildings = async () => {
    try {
      const response = await axios.get(`${API}/immeubles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuildings(response.data);
    } catch (error) {
      console.error('Error fetching buildings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const isCreating = !editingBuilding;
    try {
      if (editingBuilding) {
        await axios.put(`${API}/immeubles/${editingBuilding.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/immeubles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingBuilding(null);
      resetForm();
      fetchBuildings();
      
      // Show success notification only for new building
      if (isCreating) {
        setNotificationType('BUILDING_CREATED');
        setSuccessConfig(SUCCESS_CONFIGS.BUILDING_CREATED);
        setShowSuccessNotification(true);
      }
    } catch (error) {
      console.error('Error saving building:', error);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      num_immeuble: '',
      nom: '',
      adresse: '',
      code_postal: '',
      ville: '',
      immatriculation: '',
      nb_lots: 0,
      total_tantiemes: 10000,
      banque_nom: '',
      banque_iban: '',
      banque_bic: '',
      banque_titulaire: ''
    });
  };

  const handleEdit = (building) => {
    setEditingBuilding(building);
    setFormData({
      num_immeuble: building.num_immeuble || '',
      nom: building.nom || '',
      adresse: building.adresse || '',
      code_postal: building.code_postal || '',
      ville: building.ville || '',
      immatriculation: building.immatriculation || '',
      nb_lots: building.nb_lots || 0,
      total_tantiemes: building.total_tantiemes || 10000,
      banque_nom: building.banque_nom || '',
      banque_iban: building.banque_iban || '',
      banque_bic: building.banque_bic || '',
      banque_titulaire: building.banque_titulaire || ''
    });
    setShowModal(true);
  };

  const handleView = (building) => {
    setViewingBuilding(building);
    setShowDetailModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet immeuble ?')) {
      try {
        await axios.delete(`${API}/immeubles/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchBuildings();
      } catch (error) {
        console.error('Error deleting building:', error);
      }
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredBuildings.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredBuildings.map(b => b.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // Export to CSV
  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/immeubles/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const csvContent = response.data.csv_content;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename || 'immeubles.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Erreur lors de l\'export');
    }
  };

  // Import from CSV
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportResult(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvContent = event.target.result;
        const response = await axios.post(`${API}/immeubles/import`, 
          { csv_content: csvContent },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setImportResult({
          success: true,
          message: response.data.message,
          imported: response.data.imported,
          updated: response.data.updated,
          errors: response.data.errors || []
        });
        
        fetchBuildings();
        
        // Show success notification for successful import
        if (response.data.imported > 0 || response.data.updated > 0) {
          setShowImportModal(false);
          setNotificationType('IMPORT_BUILDING_SUCCESS');
          const totalProcessed = (response.data.imported || 0) + (response.data.updated || 0);
          setSuccessConfig({
            ...SUCCESS_CONFIGS.IMPORT_BUILDING_SUCCESS,
            message: `${totalProcessed} immeuble(s) traité(s) avec succès (${response.data.imported || 0} créé(s), ${response.data.updated || 0} mis à jour).`
          });
          setShowSuccessNotification(true);
        }
      } catch (error) {
        setImportResult({
          success: false,
          message: error.response?.data?.detail || 'Erreur lors de l\'import',
          errors: []
        });
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = 'NUM IMMEUBLE;NOM IMMEUBLE;base de repartation;LIBELLE BASE DE REPART;BASE DE REPART (TOTAL DES TANTIEMES);ADRESSE;CP;VILLE;IMMATRICULATION\n170;CLASSIC RED;CCG;CHARGES COMMUNES GENERALES;10000;258/261 rue des Fontaines;31300;TOULOUSE;78452152\n';
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_immeubles.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filteredBuildings = buildings
    .sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      if (sortOrder === 'asc') {
        return aVal.toString().localeCompare(bVal.toString());
      }
      return bVal.toString().localeCompare(aVal.toString());
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#00D3A8] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="immeubles-page">
      {/* Header - Style unifié */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#00D3A8]/10 rounded-xl">
            <Building className="h-8 w-8 text-[#00D3A8]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mes immeubles</h1>
            <p className="text-gray-500">Gérez vos copropriétés et leurs informations</p>
          </div>
        </div>
        <button
          onClick={() => { setEditingBuilding(null); resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#00D3A8] text-white rounded-full font-medium shadow-md hover:bg-[#00B892] transition-all"
          data-testid="new-building-btn"
        >
          <Plus size={18} />
          <span>Nouvel immeuble</span>
        </button>
      </div>

      {/* Actions bar */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <button 
            onClick={toggleSelectAll}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Check size={16} className={selectedIds.length === filteredBuildings.length && filteredBuildings.length > 0 ? 'text-[#00D3A8]' : 'text-gray-400'} />
            <span>Tout sélectionner</span>
          </button>
          <button 
            onClick={handleImportClick}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-2"
            data-testid="import-btn"
          >
            <Upload size={16} />
            <span>Importer</span>
          </button>
          <button 
            onClick={handleExport}
            className="px-3 py-2 border border-slate-200 rounded-lg text-xs md:text-sm text-slate-600 hover:bg-slate-50 transition-colors flex items-center space-x-2"
            data-testid="export-btn"
          >
            <Download size={16} />
            <span>Exporter</span>
          </button>
        </div>
        <div className="flex items-center space-x-2 text-xs md:text-sm text-slate-500">
          <span className="hidden sm:inline">Voir</span>
          <select 
            value={itemsPerPage} 
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="border border-slate-200 rounded-lg px-2 py-1 text-slate-700"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Table - Desktop view */}
      <div className="hidden md:block bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 overflow-x-auto">
        <table className="w-full" data-testid="buildings-table">
          <thead className="bg-slate-50 border-b border-slate-100">
            <tr>
              <th className="w-12 px-4 py-3">
                <input 
                  type="checkbox" 
                  checked={selectedIds.length === filteredBuildings.length && filteredBuildings.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-slate-300 text-[#00D3A8] focus:ring-[#00D3A8]"
                />
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                onClick={() => handleSort('num_immeuble')}
              >
                <div className="flex items-center space-x-1">
                  <span>N°</span>
                  {sortField === 'num_immeuble' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                onClick={() => handleSort('nom')}
              >
                <div className="flex items-center space-x-1">
                  <span>Nom</span>
                  {sortField === 'nom' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700"
                onClick={() => handleSort('adresse')}
              >
                <div className="flex items-center space-x-1">
                  <span>Adresse</span>
                  {sortField === 'adresse' && (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Tantièmes
              </th>
              <th className="w-32 px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredBuildings.slice(0, itemsPerPage).map((building) => (
              <tr 
                key={building.id} 
                className="hover:bg-slate-50 transition-colors"
                data-testid={`building-row-${building.id}`}
              >
                <td className="px-4 py-3">
                  <input 
                    type="checkbox"
                    checked={selectedIds.includes(building.id)}
                    onChange={() => toggleSelect(building.id)}
                    className="w-4 h-4 rounded border-slate-300 text-[#00D3A8] focus:ring-[#00D3A8]"
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="text-slate-500 font-mono text-sm">{building.num_immeuble || '-'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-medium text-slate-700">{building.nom}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-slate-600 text-sm">
                    {building.adresse}{building.adresse && building.code_postal ? ', ' : ''}{building.code_postal} {building.ville}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-slate-700 font-medium">{building.total_tantiemes?.toLocaleString('fr-FR') || '-'}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end space-x-1">
                    <button 
                      onClick={() => handleView(building)} 
                      className="p-1.5 text-slate-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Voir les détails"
                      data-testid={`view-building-${building.id}`}
                    >
                      <Eye size={16} />
                    </button>
                    <button 
                      onClick={() => handleEdit(building)} 
                      className="p-1.5 text-slate-400 hover:text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded-lg transition-colors"
                      title="Modifier"
                      data-testid={`edit-building-${building.id}`}
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      onClick={() => handleDelete(building.id)} 
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Supprimer"
                      data-testid={`delete-building-${building.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredBuildings.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Building size={40} className="mx-auto mb-3 text-slate-200" strokeWidth={1.5} />
            <p className="text-sm">Aucun immeuble trouvé</p>
          </div>
        )}

        {/* Pagination */}
        {filteredBuildings.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
            <span>{filteredBuildings.length} immeuble(s)</span>
            <div className="flex items-center space-x-2">
              <button className="px-2 py-1 hover:bg-slate-100 rounded transition-colors">←</button>
              <span className="px-3 py-1 bg-[#00D3A8] text-white rounded">1</span>
              <button className="px-2 py-1 hover:bg-slate-100 rounded transition-colors">→</button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {filteredBuildings.map((building) => (
          <div 
            key={building.id} 
            className="bg-white rounded-xl shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 p-4"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-2">
                <input 
                  type="checkbox" 
                  checked={selectedIds.includes(building.id)}
                  onChange={() => toggleSelect(building.id)}
                  className="w-4 h-4 rounded border-slate-300 text-[#00D3A8] focus:ring-[#00D3A8]"
                />
                <span className="text-xs font-semibold bg-[#00D3A8]/10 text-[#00D3A8] px-2 py-0.5 rounded">
                  N°{building.num_immeuble}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <button onClick={() => handleView(building)} className="p-1.5 text-slate-400 hover:text-blue-500">
                  <Eye size={16} />
                </button>
                <button onClick={() => handleEdit(building)} className="p-1.5 text-slate-400 hover:text-[#00D3A8]">
                  <Edit size={16} />
                </button>
                <button onClick={() => handleDelete(building.id)} className="p-1.5 text-slate-400 hover:text-red-500">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            <h3 className="font-semibold text-slate-800">{building.nom}</h3>
            <p className="text-sm text-slate-500 mt-1">
              {building.adresse}{building.code_postal ? `, ${building.code_postal}` : ''} {building.ville}
            </p>
            <div className="mt-2 text-xs text-slate-400">
              Tantièmes: <span className="font-medium text-slate-600">{building.total_tantiemes?.toLocaleString('fr-FR') || '-'}</span>
            </div>
          </div>
        ))}
        
        {filteredBuildings.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <Building size={40} className="mx-auto mb-3 text-slate-200" strokeWidth={1.5} />
            <p className="text-sm">Aucun immeuble trouvé</p>
          </div>
        )}
      </div>

      {/* Modal Édition/Création */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-4 md:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingBuilding ? 'Modifier l\'immeuble' : 'Nouvel immeuble'}
                </h2>
                {editingBuilding && (
                  <p className="text-sm text-slate-500 mt-1">ID: {editingBuilding.id?.slice(0, 8)}...</p>
                )}
              </div>
              <button 
                onClick={() => { setShowModal(false); setEditingBuilding(null); }}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Section Identification */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">
                  <Hash size={16} className="mr-2 text-[#00D3A8]" />
                  Identification
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">N° Immeuble</label>
                    <input 
                      type="text" 
                      value={formData.num_immeuble} 
                      onChange={(e) => setFormData({...formData, num_immeuble: e.target.value})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      placeholder="Ex: 170"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Immatriculation</label>
                    <input 
                      type="text" 
                      value={formData.immatriculation} 
                      onChange={(e) => setFormData({...formData, immatriculation: e.target.value})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      placeholder="Ex: 78452152"
                    />
                  </div>
                </div>
              </div>

              {/* Section Informations générales */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Nom de l'immeuble *</label>
                <input 
                  type="text" 
                  value={formData.nom} 
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all" 
                  placeholder="Ex: Résidence Les Lilas"
                  required 
                />
              </div>

              {/* Section Adresse */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">
                  <MapPin size={16} className="mr-2 text-[#00D3A8]" />
                  Adresse
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Adresse</label>
                    <input 
                      type="text" 
                      value={formData.adresse} 
                      onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      placeholder="Ex: 12 rue des Fleurs"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Code postal</label>
                      <input 
                        type="text" 
                        value={formData.code_postal} 
                        onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                        placeholder="Ex: 75001"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Ville</label>
                      <input 
                        type="text" 
                        value={formData.ville} 
                        onChange={(e) => setFormData({...formData, ville: e.target.value})}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                        placeholder="Ex: Paris"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Caractéristiques */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center">
                  <Building size={16} className="mr-2 text-[#00D3A8]" />
                  Caractéristiques
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Nombre de lots</label>
                    <input 
                      type="number" 
                      value={formData.nb_lots} 
                      onChange={(e) => setFormData({...formData, nb_lots: parseInt(e.target.value) || 0})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Total tantièmes (CCG)</label>
                    <input 
                      type="number" 
                      value={formData.total_tantiemes} 
                      onChange={(e) => setFormData({...formData, total_tantiemes: parseInt(e.target.value) || 10000})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Coordonnées bancaires de la copropriété */}
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                <h4 className="text-sm font-semibold text-blue-800 mb-4 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Coordonnées bancaires de la copropriété
                </h4>
                <p className="text-xs text-blue-600 mb-4">Ces informations seront visibles par les coproprietaires pour effectuer leurs virements</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Nom de la banque</label>
                    <input 
                      type="text" 
                      value={formData.banque_nom} 
                      onChange={(e) => setFormData({...formData, banque_nom: e.target.value})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      placeholder="Ex: Crédit Agricole"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Titulaire du compte</label>
                    <input 
                      type="text" 
                      value={formData.banque_titulaire} 
                      onChange={(e) => setFormData({...formData, banque_titulaire: e.target.value})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all"
                      placeholder="Ex: Syndicat des coproprietaires..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">IBAN *</label>
                    <input 
                      type="text" 
                      value={formData.banque_iban} 
                      onChange={(e) => setFormData({...formData, banque_iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').replace(/(.{4})/g, '$1 ').trim()})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all font-mono text-sm"
                      placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">BIC/SWIFT</label>
                    <input 
                      type="text" 
                      value={formData.banque_bic} 
                      onChange={(e) => setFormData({...formData, banque_bic: e.target.value.toUpperCase()})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#00D3A8]/20 focus:border-[#00D3A8] transition-all font-mono"
                      placeholder="Ex: AGRIFRPP"
                    />
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100">
                <button 
                  type="button" 
                  onClick={() => { setShowModal(false); setEditingBuilding(null); }} 
                  className="px-5 py-2.5 border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 font-medium transition-colors"
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="px-6 py-2.5 text-white rounded-full font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-50 flex items-center space-x-2"
                  style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Enregistrement...</span>
                    </>
                  ) : (
                    <span>Enregistrer</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Détail */}
      {showDetailModal && viewingBuilding && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {viewingBuilding.nom}
                </h2>
                {viewingBuilding.num_immeuble && (
                  <p className="text-sm text-slate-500 mt-1">N° {viewingBuilding.num_immeuble}</p>
                )}
              </div>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Informations générales */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Adresse</p>
                  <p className="text-slate-800 font-medium">
                    {viewingBuilding.adresse || '-'}<br />
                    {viewingBuilding.code_postal} {viewingBuilding.ville}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Immatriculation</p>
                  <p className="text-slate-800 font-medium font-mono">{viewingBuilding.immatriculation || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Nombre de lots</p>
                  <p className="text-2xl font-bold text-slate-800">{viewingBuilding.nb_lots || 0}</p>
                </div>
                <div className="bg-[#00D3A8]/10 rounded-lg p-4">
                  <p className="text-xs text-[#00D3A8] uppercase tracking-wider mb-1">Total tantièmes CCG</p>
                  <p className="text-2xl font-bold text-[#00D3A8]">{viewingBuilding.total_tantiemes?.toLocaleString('fr-FR') || 0}</p>
                </div>
              </div>

              {/* Bases de répartition */}
              {viewingBuilding.bases_repartition && Object.keys(viewingBuilding.bases_repartition).length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">Bases de répartition</h3>
                  <div className="bg-slate-50 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Code</th>
                          <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Tantièmes</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {Object.entries(viewingBuilding.bases_repartition).map(([code, value]) => (
                          <tr key={code} className={code === 'CCG' ? 'bg-[#00D3A8]/5' : ''}>
                            <td className="px-4 py-2 text-slate-700 font-medium">{code}</td>
                            <td className="px-4 py-2 text-right text-slate-600">{value?.toLocaleString('fr-FR')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Coordonnées bancaires */}
              {(viewingBuilding.banque_iban || viewingBuilding.banque_nom) && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Coordonnées bancaires de la copropriété
                  </h3>
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                    <div className="grid grid-cols-2 gap-4">
                      {viewingBuilding.banque_nom && (
                        <div>
                          <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Banque</p>
                          <p className="text-slate-800 font-medium">{viewingBuilding.banque_nom}</p>
                        </div>
                      )}
                      {viewingBuilding.banque_titulaire && (
                        <div>
                          <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Titulaire</p>
                          <p className="text-slate-800 font-medium">{viewingBuilding.banque_titulaire}</p>
                        </div>
                      )}
                      {viewingBuilding.banque_iban && (
                        <div className="col-span-2">
                          <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">IBAN</p>
                          <p className="text-slate-800 font-mono font-medium tracking-wider">{viewingBuilding.banque_iban}</p>
                        </div>
                      )}
                      {viewingBuilding.banque_bic && (
                        <div>
                          <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">BIC/SWIFT</p>
                          <p className="text-slate-800 font-mono font-medium">{viewingBuilding.banque_bic}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-slate-100">
              <button 
                onClick={() => setShowDetailModal(false)}
                className="px-5 py-2.5 border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 font-medium transition-colors"
              >
                Fermer
              </button>
              <button 
                onClick={() => {
                  setShowDetailModal(false);
                  handleEdit(viewingBuilding);
                }}
                className="px-5 py-2.5 text-white rounded-full font-medium shadow-md hover:shadow-lg transition-all flex items-center space-x-2"
                style={{ background: 'linear-gradient(90deg, #00D3A8 0%, #00C7A1 100%)' }}
              >
                <Edit size={16} />
                <span>Modifier</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Importer des immeubles
              </h2>
              <button onClick={() => setShowImportModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-medium text-slate-700 mb-2 flex items-center">
                  <FileText size={18} className="mr-2 text-[#00D3A8]" />
                  Format du fichier CSV
                </h3>
                <p className="text-sm text-slate-500 mb-3">
                  Séparateur : <strong>point-virgule (;)</strong>. Colonnes requises :
                </p>
                <code className="text-xs bg-white px-3 py-2 rounded border border-slate-200 block overflow-x-auto">
                  NUM IMMEUBLE; NOM IMMEUBLE; ADRESSE; CP; VILLE; IMMATRICULATION; base de repartation; BASE DE REPART (TOTAL DES TANTIEMES)
                </code>
                <p className="text-xs text-slate-400 mt-2">
                  Note: Si plusieurs lignes par immeuble (bases de répartition), seul le total CCG sera utilisé comme tantièmes.
                </p>
                <button 
                  onClick={downloadTemplate}
                  className="mt-3 text-sm text-[#00D3A8] hover:text-[#00C7A1] font-medium"
                >
                  Télécharger le modèle CSV
                </button>
              </div>

              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-[#00D3A8] transition-colors">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload size={32} className="mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-600 font-medium">Cliquez pour sélectionner un fichier CSV</p>
                  <p className="text-sm text-slate-400 mt-1">ou glissez-déposez le fichier ici</p>
                </label>
              </div>

              {importing && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#00D3A8] border-t-transparent mr-3"></div>
                  <span className="text-slate-600">Import en cours...</span>
                </div>
              )}

              {importResult && (
                <div className={`rounded-lg p-4 ${importResult.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-start">
                    {importResult.success ? (
                      <Check size={20} className="text-emerald-500 mr-2 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle size={20} className="text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    )}
                    <div>
                      <p className={`font-medium ${importResult.success ? 'text-emerald-700' : 'text-red-700'}`}>
                        {importResult.message}
                      </p>
                      {importResult.errors && importResult.errors.length > 0 && (
                        <ul className="mt-2 text-sm text-red-600 space-y-1">
                          {importResult.errors.slice(0, 5).map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                          {importResult.errors.length > 5 && (
                            <li className="text-red-500">... et {importResult.errors.length - 5} autres erreurs</li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button 
                onClick={() => setShowImportModal(false)}
                className="px-5 py-2.5 border border-slate-200 rounded-full text-slate-600 hover:bg-slate-50 font-medium transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {successConfig && (
        <SuccessNotification
          isOpen={showSuccessNotification}
          onClose={() => {
            setShowSuccessNotification(false);
            setSuccessConfig(null);
          }}
          title={successConfig.title}
          message={successConfig.message}
          nextStep={successConfig.nextStep}
          notificationType={notificationType}
          autoCloseDelay={8000}
        />
      )}
    </div>
  );
};

export default Immeubles;
